import {cohortApiUrl, handleJsonResponse, UserDetails} from "../../actions/api";
import {toURL} from "../../reducers/util";
import {Project, SharedProject} from "../../types/Project";
import {Query} from "../HealthcareUsageReport/types/CohortQuery";
import {CONFIG} from "../../util/config";

export type PostProject = Required<Pick<Project, 'title' | 'description'>>
export type PatchProject = Required<Pick<Project, 'title'>>

export const apiFetchProjects = async (): Promise<Project[]> => {
    const url = cohortApiUrl() + '/studies'
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return handleJsonResponse(result)
}

export const apiFetchExampleProjects = async (): Promise<Project[]> => {
    const url = cohortApiUrl() + '/studies?example=true'
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return handleJsonResponse(result)
}

export const apiFetchSharedProjects = async(): Promise<SharedProject[]> => {
    const url = cohortApiUrl() + '/studies?shared=true'
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return handleJsonResponse(result)
}

export const apiFetchSharedProjectsUnreadCount = async(): Promise<{unreadCount: number}> => {
    const url = cohortApiUrl() + '/unread-shared-projects-count'
    const result = await fetch(url, {
        ...(CONFIG.isDev && { credentials: 'include' })
    })
    return handleJsonResponse(result)
}

export const apiDeleteStudy = async (studyId: number): Promise<Response> => {
    const url = toURL('/study?studyId=' + studyId)
    return await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const apiCreateStudy = async (project: PostProject): Promise<Project> => {
    const url = toURL('/study')
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(project),
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return handleJsonResponse(response)
}

export const apiUpdateStudy = async (study: Project): Promise<Response> => {
    const url = `${cohortApiUrl()}/study/${study.id}`
    const content = {
        title: study.title,
        description: study.description,
    }

    return fetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const apiUpdateQuery = async (query: Query): Promise<Query> => {
    const url = `${cohortApiUrl()}/query/${query.id}`
    const content = {
        title: query.title,
        unread: query.unread
    }

    const response = await fetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return handleJsonResponse(response)
}

export const apiDeleteQuery = async (queryId: number): Promise<Response> => {
    const url = toURL(`/query?queryId=${queryId}`)
    return fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const apiSaveQuery = async (studyId: number, query: Query): Promise<Query> => {
    const url = toURL(`/query?studyId=${studyId}`)
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(query, null, 2),
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return response.json()
}

export const apiRunQuery = async (queryId: number, queryDescription: string): Promise<Response> => {
    const url = toURL(`/runQuery?queryId=${queryId}`)
    const body = {
        queryId: queryId,
        description: queryDescription
    }
    return fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body, null, 2),
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const apiStopQuery = async (sparkQueryId: number): Promise<Response> => {
    const url = toURL(`/archiveSparkQuery/${sparkQueryId}`)

    return fetch(url, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })
}

export const apiRefreshQueryRequests = (queryIDs: number[]) => {
    const url = toURL('/refresh?' + queryIDs.map(i => 'queryId=' + i).join("&"))
    return fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    }).then(response => response.json())
}

export const apiFetchCohortBuilderColleagues = async(): Promise<UserDetails[]> => {
    const url = toURL('/colleagues')
    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return handleJsonResponse(response)
}

export const apiShareProject = async(projectId: number, username: string): Promise<void> => {
    const url = toURL('/share')
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            projectId,
            shareWith: username
        }),
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return handleJsonResponse(response)
}

export const apiMarkSharedProjectRead = async(projectId: number): Promise<void> => {
    const url = toURL(`/mark-shared-project-read/${projectId}`)
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        ...(CONFIG.isDev && { credentials: 'include' })
    })

    return handleJsonResponse(response)
}

export const apiFetchCohortDataset = async (cohortId: number): Promise<string> => {
    const url = cohortApiUrl() + `/cohortDataset?cohortId=${cohortId}`
    return (await fetch(url)).text();
}